import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  InputAdornment,
  Chip,
} from '@mui/material';
import { 
  Domain as DomainIcon,
  ShoppingCart as BuyIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import axios from 'axios';
import SetupLayout from './SetupLayout';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

const DomainSetup: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [domainAvailable, setDomainAvailable] = useState<boolean | null>(null);

  const signupData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
  const selectedPlan = signupData.selectedPlan || { name: 'Free' };

  useEffect(() => {
    // Check if we have the required data from previous steps
    const storedData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
    console.log('DomainSetup - storedData:', storedData);
    // Only require organization and token - organizationId comes from verify-otp
    if (!storedData.organization || !storedData.token) {
      console.log('Missing required data, redirecting to /signup');
      navigate('/signup');
    }
  }, [navigate]);

  const validateDomain = (value: string) => {
    const domainRegex = /^(?!-)([a-zA-Z0-9-]{1,63}(?<!-)\.)+[a-zA-Z]{2,}$/;
    return domainRegex.test(value);
  };

  const checkDomainAvailability = async () => {
    if (!domain || !validateDomain(domain)) {
      setError('Please enter a valid domain name');
      return;
    }

    setChecking(true);
    setError('');
    setDomainAvailable(null);

    try {
      const response = await axios.post(`${API_URL}/signup/check-domain`, { domain });
      const result = response.data.data || response.data;
      setDomainAvailable(result.available);
      if (!result.available) {
        setError('This domain is already registered in our system');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error checking domain availability');
    } finally {
      setChecking(false);
    }
  };

  const handleContinue = async () => {
    if (!domain || !validateDomain(domain)) {
      setError('Please enter a valid domain name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Read fresh data from sessionStorage
      const currentData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
      console.log('DomainSetup handleContinue - currentData:', currentData);
      
      const response = await axios.post(`${API_URL}/signup/add-domain`, {
        organizationId: String(currentData.organizationId),
        domain: domain,
        token: currentData.token,
        plan: currentData.selectedPlan?.id || currentData.plan || 'free',
      });

      if (response.data.success || response.data.data) {
        setSuccess('Domain added successfully!');
        // Handle nested response: response.data.data.data contains domainId
        const responseData = response.data.data?.data || response.data.data || response.data;
        console.log('DomainSetup response:', response.data);
        console.log('Extracted domainId:', responseData.domainId);
        
        sessionStorage.setItem('signupData', JSON.stringify({
          ...currentData,
          domain: domain,
          domainId: responseData.domainId,
        }));
        setTimeout(() => navigate('/setup/admin-user'), 1000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add domain');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    sessionStorage.setItem('signupData', JSON.stringify({
      ...signupData,
      domain: `${signupData.organization?.toLowerCase().replace(/\s+/g, '')}.cruvzmail.com`,
      skipDomain: true,
    }));
    navigate('/setup/admin-user');
  };

  const popularExtensions = ['.com', '.io', '.co', '.net', '.org'];

  return (
    <SetupLayout activeStep={3} backPath="/setup/verify">
      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', py: 6 }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', width: '100%', px: 3, display: 'flex', gap: 6 }}>
          {/* Left - Form */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
              <DomainIcon sx={{ fontSize: 30, color: '#dc2626' }} />
            </Box>

            <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', mb: 1, fontFamily: 'Inter, sans-serif' }}>
              Set up your domain
            </Typography>
            <Typography sx={{ color: '#6b7280', mb: 4, fontFamily: 'Inter, sans-serif' }}>
              Use your own domain or get a free subdomain
            </Typography>

            {/* Tabs */}
            <Tabs
              value={tabValue}
              onChange={(_, v) => setTabValue(v)}
              sx={{
                mb: 4,
                '& .MuiTabs-indicator': { bgcolor: '#dc2626' },
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, color: '#6b7280', fontFamily: 'Inter, sans-serif' },
                '& .Mui-selected': { color: '#dc2626 !important' },
              }}
            >
              <Tab icon={<DomainIcon />} label="Use Your Domain" iconPosition="start" />
              <Tab icon={<BuyIcon />} label="Buy Domain" iconPosition="start" />
            </Tabs>

            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{success}</Alert>}

            {tabValue === 0 ? (
              <Box>
                <Typography sx={{ fontWeight: 600, color: '#111827', mb: 2, fontFamily: 'Inter, sans-serif' }}>
                  Enter your domain name
                </Typography>
                <TextField
                  fullWidth
                  placeholder="example.com"
                  value={domain}
                  onChange={(e) => {
                    setDomain(e.target.value.toLowerCase());
                    setDomainAvailable(null);
                    setError('');
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {checking ? (
                          <CircularProgress size={20} />
                        ) : domainAvailable === true ? (
                          <CheckIcon sx={{ color: '#22c55e' }} />
                        ) : domainAvailable === false ? (
                          <CloseIcon sx={{ color: '#ef4444' }} />
                        ) : null}
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#f9fafb',
                      '& fieldset': { borderColor: '#e5e7eb' },
                      '&:hover fieldset': { borderColor: '#dc2626' },
                      '&.Mui-focused fieldset': { borderColor: '#dc2626' },
                    },
                  }}
                />
                <Button
                  onClick={checkDomainAvailability}
                  disabled={!domain || checking}
                  startIcon={<SearchIcon />}
                  sx={{ mb: 3, color: '#dc2626', textTransform: 'none', fontWeight: 600 }}
                >
                  Check Availability
                </Button>

                <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                  Make sure you have access to DNS settings for your domain
                </Alert>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleContinue}
                  disabled={loading || !domain || !validateDomain(domain)}
                  sx={{ py: 1.5, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }, fontWeight: 600, fontSize: '1rem', textTransform: 'none', borderRadius: 2, mb: 2 }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Continue with Domain'}
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleSkip}
                  sx={{ py: 1.5, borderColor: '#e5e7eb', color: '#6b7280', '&:hover': { borderColor: '#dc2626', bgcolor: 'transparent' }, fontWeight: 600, textTransform: 'none', borderRadius: 2 }}
                >
                  Skip & Use Free Subdomain
                </Button>
              </Box>
            ) : (
              <Box>
                <Typography sx={{ fontWeight: 600, color: '#111827', mb: 2, fontFamily: 'Inter, sans-serif' }}>
                  Search for available domains
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Search domain name..."
                  value={domain}
                  onChange={(e) => setDomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#f9fafb',
                      '& fieldset': { borderColor: '#e5e7eb' },
                      '&:hover fieldset': { borderColor: '#dc2626' },
                      '&.Mui-focused fieldset': { borderColor: '#dc2626' },
                    },
                  }}
                />
                
                <Typography sx={{ color: '#6b7280', mb: 2, fontFamily: 'Inter, sans-serif', fontSize: '0.875rem' }}>
                  Popular extensions
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                  {popularExtensions.map((ext) => (
                    <Chip
                      key={ext}
                      label={domain ? `${domain}${ext}` : ext}
                      onClick={() => domain && setDomain(`${domain}${ext}`)}
                      sx={{ bgcolor: '#f3f4f6', color: '#374151', '&:hover': { bgcolor: '#fef2f2', color: '#dc2626' } }}
                    />
                  ))}
                </Box>

                <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                  Domain purchase feature coming soon! For now, you can use your existing domain or get a free subdomain.
                </Alert>

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleSkip}
                  sx={{ py: 1.5, borderColor: '#e5e7eb', color: '#6b7280', '&:hover': { borderColor: '#dc2626', bgcolor: 'transparent' }, fontWeight: 600, textTransform: 'none', borderRadius: 2 }}
                >
                  Use Free Subdomain Instead
                </Button>
              </Box>
            )}
          </Box>

          {/* Right - Info Panel */}
          <Box sx={{ width: 350, display: { xs: 'none', md: 'block' } }}>
            <Box sx={{ bgcolor: '#f8fafc', borderRadius: 3, p: 4, border: '1px solid #e5e7eb' }}>
              <Typography sx={{ fontWeight: 600, color: '#111827', mb: 3, fontFamily: 'Inter, sans-serif' }}>
                Why you need a domain
              </Typography>
              
              {[
                { title: 'Professional Emails', desc: 'Get emails like name@yourdomain.com' },
                { title: 'Brand Identity', desc: 'Build trust with a custom email address' },
                { title: 'Full Control', desc: 'Manage all your email settings' },
              ].map((item, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.5 }}>
                    <CheckIcon sx={{ fontSize: 14, color: '#22c55e' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '0.875rem', fontFamily: 'Inter, sans-serif' }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ color: '#6b7280', fontSize: '0.8rem', fontFamily: 'Inter, sans-serif' }}>
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}

              <Box sx={{ mt: 4, p: 3, bgcolor: '#fef2f2', borderRadius: 2 }}>
                <Typography sx={{ fontWeight: 600, color: '#dc2626', mb: 1, fontFamily: 'Inter, sans-serif' }}>
                  Don't have a domain?
                </Typography>
                <Typography sx={{ color: '#6b7280', fontSize: '0.875rem', fontFamily: 'Inter, sans-serif' }}>
                  No problem! You can use a free subdomain like <strong>{signupData.organization?.toLowerCase().replace(/\s+/g, '') || 'yourorg'}.cruvzmail.com</strong>
                </Typography>
              </Box>

              {/* Selected Plan */}
              <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid #e5e7eb' }}>
                <Typography sx={{ color: '#6b7280', fontSize: '0.75rem', fontFamily: 'Inter, sans-serif' }}>
                  Selected Plan
                </Typography>
                <Typography sx={{ fontWeight: 700, color: '#111827', fontFamily: 'Inter, sans-serif' }}>
                  {selectedPlan.name}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </SetupLayout>
  );
};

export default DomainSetup;
